import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import SellerRegisterForm from "./SellerRegisterForm";
import { postFileUpload, getData, postData } from "../../Webservice/Webservice";
import { Box, Typography, Button, Alert, Paper, styled, Container } from "@mui/material";
import { useLocation } from "react-router-dom";
import { MdCheckCircle } from 'react-icons/md';

const StyledRoot = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundImage: 'url("/img/seller_bg.png")', // New premium unique background
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)', // Dark overlay for readability
    zIndex: 1,
  },
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  color: '#fff',
}));

const InviteBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  background: 'rgba(255, 209, 2, 0.9)',
  color: '#000',
  padding: '6px 16px',
  borderRadius: '20px',
  marginBottom: theme.spacing(2),
  fontWeight: 700,
  fontSize: '0.85rem',
  boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
}));

const initialSellerForm = {
  name: "",
  email: "",
  password: "",
  mobile: "",
  contactPerson2Name: "",
  contactPerson2Email: "",
  contactPerson2Mobile: "",
  contactPerson2Password: "",
  business_name: "",
  businessAddress: "",
  sellerLicenseNumber: "",
  businessCity: "",
  businessState: "",
  businessPincode: "",
  gstNumber: "",
  businessPAN: "",
  addressProof: null,
  gstProof: null,
  businessPanProof: null,
  shopActLicense: null,
  msmeUdyamCertificate: null,
  cancelledCheque: null,
  eSignature: null,
  bankAccountHolder: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankName: "",
  bankUpiId: "",
  storeDescription: "",
  storeAddress: "",
  storeArea: "",
  storeNearbyLandmark: "",
  storeCity: "",
  storeState: "",
  storePincode: "",
  storeLatitude: "",
  storeLongitude: "",
  status: "pending"
};

const SellerRegisterPage = () => {
  console.log("SellerRegisterPage - Component loaded");
  const [sellerForm, setSellerForm] = useState(initialSellerForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sellerId = queryParams.get('id') || (location.search.startsWith('?') ? location.search.substring(1) : location.search);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    const checkInviteStatus = async () => {
      if (sellerId) {
        setCheckingStatus(true);
        try {
          const response = await getData(`seller/invite-info/${sellerId}`, null, null, false, '', true);

          if (response && response.isRegistrationComplete) {
            setIsAlreadySubmitted(true);
          } else if (response && response.draftData) {
            // Restore draft data if available and registration is not complete
            setSellerForm(prev => ({ ...prev, ...response.draftData }));
          }
        } catch (error) {
          console.error("Error checking invite status:", error);
        } finally {
          setCheckingStatus(false);
        }
      }
    };
    checkInviteStatus();
  }, [sellerId]);

  const handleFormChange = e => {
    const { name, value } = e.target;
    setSellerForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = e => {
    const { name } = e.target;
    const file = e.target.files[0];
    setSellerForm(prev => ({ ...prev, [name]: file }));
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'name', 'email', 'password', 'mobile', 'business_name', 'businessAddress',
      'businessCity', 'businessState', 'businessPincode',
      'bankAccountHolder', 'bankAccountNumber', 'bankIfsc', 'bankName'
    ];

    requiredFields.forEach(field => {
      if (!sellerForm[field]?.toString().trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    if (sellerForm.bankIfsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(sellerForm.bankIfsc.trim())) {
      newErrors.bankIfsc = 'Invalid IFSC format (e.g. ABCD0123456)';
    }

    if (sellerForm.contactPerson2Name) {
      if (!sellerForm.contactPerson2Email) newErrors.contactPerson2Email = 'Email required for access';
      if (!sellerForm.contactPerson2Mobile) newErrors.contactPerson2Mobile = 'Mobile required for access';
      if (!sellerForm.contactPerson2Password) newErrors.contactPerson2Password = 'Password required for access';
    }

    if (!sellerForm.storeLatitude || !sellerForm.storeLongitude) {
      newErrors.storeLatitude = 'Please select store location on map';
    }

    const isAnyDocUploaded = ['addressProof', 'gstProof', 'businessPanProof', 'shopActLicense', 'msmeUdyamCertificate', 'cancelledCheque', 'eSignature'].some(field => !!sellerForm[field]);
    if (!isAnyDocUploaded) {
      newErrors.addressProof = 'At least one document is mandatory';
      setSubmitError("Please upload at least one document.");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    if (!sellerId) return;

    setIsSubmitting(true);
    try {
      // Create a copy of form without file objects for saving text draft
      const draftData = { ...sellerForm };
      // Remove file instances as they can't be saved as JSON easily 
      // (usually drafts save text fields, files are handled separately or on final submit)
      const fileFields = ['addressProof', 'gstProof', 'businessPanProof', 'shopActLicense', 'msmeUdyamCertificate', 'cancelledCheque', 'eSignature'];
      fileFields.forEach(field => delete draftData[field]);

      const response = await postData("seller/draft", { sellerId, draftData }, null, true, true);
      if (response) {
        // toast inside postData will handle success if status is 200, 
        // but we added a local one too. Let's check postData logic.
        // postData has toast.success(responseData.message).
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft.");
    }
    setIsSubmitting(false);
  };

  const handleRegisterSeller = async () => {
    setSubmitError("");
    if (!validateForm()) {
      setSubmitError("Please fill all required fields and select location on map.");
      window.scrollTo(0, 0);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(sellerForm).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      });

      if (sellerId) {
        formData.append("sellerId", sellerId);
      }

      const response = await postFileUpload("seller/register", formData, null, true);
      if (response) {
        setIsAlreadySubmitted(true);
        setSellerForm(initialSellerForm);
      }
    } catch (error) {
      console.error("Error registering seller:", error);
      setSubmitError("Error registering seller. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <StyledRoot>
      <StyledContainer maxWidth="lg">
        {!isAlreadySubmitted && (
          <HeaderBox>
            <Typography
              variant="h3"
              fontWeight={900}
              sx={{
                fontFamily: 'Mulish, sans-serif',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                mb: 1
              }}
            >
              Become a Seller
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Mulish, sans-serif',
                opacity: 0.9,
                fontWeight: 400
              }}
            >
              Join the True Value For Money marketplace and grow your business today.
            </Typography>
          </HeaderBox>
        )}

        <Box sx={{ maxWidth: 1000, margin: '0 auto' }}>
          {submitError && (
            <Alert
              severity="error"
              variant="filled"
              sx={{ mb: 3, borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              {submitError}
            </Alert>
          )}

          {isAlreadySubmitted ? (
            <Paper
              sx={{
                p: { xs: 4, md: 8 },
                textAlign: 'center',
                borderRadius: '24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                background: 'rgba(255, 255, 255, 1)',
                mt: 4
              }}
            >
              <Box mb={4}>
                <img
                  src="https://cdn-icons-png.flaticon.com/512/148/148767.png"
                  alt="Success"
                  style={{ width: '120px' }}
                />
              </Box>
              <Typography
                variant="h3"
                fontWeight={900}
                gutterBottom
                sx={{ fontFamily: 'Mulish, sans-serif', color: '#2e7d32' }}
              >
                Registration Details
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mb: 2, fontFamily: 'Mulish, sans-serif', color: '#1f2937' }}
              >
                Your application was submitted already
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  fontFamily: 'Mulish, sans-serif',
                  fontSize: '1.2rem',
                  maxWidth: '700px',
                  margin: '0 auto 32px',
                  color: '#4b5563',
                  lineHeight: 1.6
                }}
              >
                Our records show that your application has already been received and is currently being processed.
                You will receive an update at your registered email address soon.
              </Typography>
              {/* <Button
                variant="outlined"
                onClick={() => window.location.replace('/')}
                sx={{
                  borderColor: '#1f2937',
                  color: '#1f2937',
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  fontWeight: 700,
                  '&:hover': { borderColor: '#000', backgroundColor: 'rgba(0,0,0,0.05)' }
                }}
              >
                Go to Home
              </Button> */}
            </Paper>
          ) : (
            <>
              <SellerRegisterForm
                sellerForm={sellerForm}
                handleFormChange={handleFormChange}
                handleFileUpload={handleFileUpload}
                errors={errors}
                setErrors={setErrors}
                handleRegisterSeller={handleRegisterSeller}
                handleSaveDraft={handleSaveDraft}
                isSubmitting={isSubmitting}
              />
            </>
          )}
        </Box>
      </StyledContainer>
    </StyledRoot>
  );
};

export default SellerRegisterPage;
